// ONBOARDING-P3: Operator › Onboarding (`/operator/onboarding`): the workspace clients that have an
// onboarding case (from the workspace client list), with status, plan, intake progress, the
// deadline and the next follow-up; each opens the case detail.
import {
  Badge,
  EmptyState,
  LocationHeader,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ClipboardListIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { OpsBody, OpsFrame, operatorCrumb, RowSub, TABLE_ROW_HOVER } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { CASE_STATUS, day, FOLLOWUP_KIND } from "./onboarding-labels";

export function OnboardingCasesPage({ api }: { api: Api }) {
  const q = useQuery({
    queryKey: ["operator", "workspace", "clients"],
    queryFn: () => api.client.operator.workspace.clients.list(),
  });
  // Lane A (beta shared contracts §3.1): a case stays here until it is handed over (or cancelled).
  const rows = (q.data?.data ?? []).flatMap((r) =>
    r.onboarding.case && !r.onboarding.case.handover_at && r.onboarding.case.status !== "cancelled"
      ? [{ company: r.company, c: r.onboarding.case }]
      : [],
  );
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Onboarding" }]}
        icon={ClipboardListIcon}
        title="Onboarding"
        count={q.isPending ? undefined : rows.length}
      />
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading onboarding cases" />
        ) : q.isError ? (
          <LoadError what="Onboarding cases" error={q.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={ClipboardListIcon}
            title="No onboarding cases"
            description="Creating a client with a plan opens its onboarding case; it appears here."
          />
        ) : (
          <Table data-slot="operator-onboarding-cases">
            <TableCaption>Clients in onboarding, from your client list.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Client</TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col">Plan</TableHead>
                <TableHead scope="col">Intake</TableHead>
                <TableHead scope="col">Due</TableHead>
                <TableHead scope="col">Next follow-up</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ company, c }) => {
                const s = CASE_STATUS[c.status] ?? { label: c.status, variant: "neutral" as const };
                return (
                  <TableRow key={company.id} data-case={c.case_id} className={TABLE_ROW_HOVER}>
                    <TableHead scope="row" className="font-normal">
                      <Link
                        to="/operator/onboarding/$companyId"
                        params={{ companyId: company.id }}
                        className="font-medium underline-offset-4 hover:underline"
                      >
                        {company.name}
                      </Link>
                      {c.operator_alerted ? <RowSub>Needs your call</RowSub> : null}
                      {c.ready_at ? <RowSub>Ready for handover</RowSub> : null}
                    </TableHead>
                    <TableCell>
                      <Badge variant={s.variant}>{s.label}</Badge>
                    </TableCell>
                    <TableCell>{c.plan_key}</TableCell>
                    <TableCell>
                      {c.percent}%{c.missing > 0 ? <RowSub>{c.missing} missing</RowSub> : null}
                    </TableCell>
                    <TableCell>
                      {day(c.due_at)}
                      {c.overdue ? (
                        <Badge variant="danger" className="ml-2">
                          Overdue
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {c.next_followup
                        ? `${FOLLOWUP_KIND[c.next_followup.kind] ?? c.next_followup.kind} · ${day(c.next_followup.due_at)}`
                        : "None"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}
